import { z } from 'zod';
import { MAX_MAP_IMAGES, MAP_IMAGE_ASSET_ID_PATTERN, isMapImageDataUrl } from './map-image.js';
import { normalizeServiceAreaConfig } from './service-area-config.js';

const point = z.object({ lat: z.number().finite().min(-90).max(90), lng: z.number().finite().min(-540).max(540) }).strict();
const image = z.object({
  id: z.string().regex(/^[A-Za-z0-9_-]{1,100}$/), assetId: z.string().regex(MAP_IMAGE_ASSET_ID_PATTERN),
  dataUrl: z.string().refine(isMapImageDataUrl), points: z.tuple([point, point, point, point]), opacity: z.number().min(0.1).max(1),
  group: z.array(z.string().max(80)).max(6).optional(), locked: z.boolean().optional(), hidden: z.boolean().optional(), name: z.string().max(120).optional(),
}).strict();
const serviceArea = z.json().nullable().transform((value, context) => {
  if (value === null) return null;
  const parsed = normalizeServiceAreaConfig(value);
  if (!parsed) { context.addIssue({ code: 'custom', message: 'Invalid service area in map file.' }); return z.NEVER; }
  return parsed;
});

/** Portable map content. Account IDs, access settings, and server references are excluded. */
export const mapFileStateSchema = z.object({
  version: z.literal(1), center: point, zoom: z.number().finite().min(0).max(24), rotation: z.number().finite(),
  basemap: z.string().min(1).max(100), globeCamera: z.object({ pitch: z.number().min(0).max(60), bearing: z.number().min(-180).max(180), terrain: z.boolean().optional() }).strict().nullable(),
  showLayerLegend: z.boolean(), serviceAreaConfig: serviceArea, activeDrawingLayerId: z.string().regex(/^[a-z0-9_-]{1,64}$/i),
  title: z.string().max(500).optional(), images: z.array(image).max(MAX_MAP_IMAGES),
}).strict();
export type MapFileState = z.infer<typeof mapFileStateSchema>;
